import {useMemo, useState} from 'react';
import {RotateCcw, SlidersHorizontal} from 'lucide-react';
import {ListingCard} from '../components/ListingCard';
import {ListingDetail} from '../components/ListingDetail';
import {useI18n} from '../i18n';
import {money, number} from '../lib/format';
import {useIntent, type MarketIntent} from '../shell/AppLink';
import {useStore} from '../store/StoreContext';
import {
  AMENITIES,
  DISTRICTS,
  LISTING_TYPE,
  PROVINCES,
  districtsOf,
  has360,
  isPublic,
  pricePerM2,
  type Listing,
  type ListingType,
  type Purpose,
} from '../store/types';
import {
  Button,
  ChipToggle,
  CheckRow,
  EmptyState,
  Field,
  Panel,
  RangeField,
  Screen,
  SearchInput,
  Segmented,
  Select,
  Toolbar,
} from '../ui/primitives';

type Sort = 'moi' | 'gia-tang' | 'gia-giam' | 'dien-tich' | 'gia-m2';

interface Filters {
  purpose: Purpose;
  query: string;
  province: string;
  district: string;
  type: string;
  /** VND; the slider's maximum means "no limit". */
  priceMax: number;
  areaMin: number;
  bedrooms: number;
  amenities: string[];
  only360: boolean;
  onlyVerified: boolean;
  sort: Sort;
}

/** Sale prices and rents are three orders of magnitude apart — so are the sliders. */
const PRICE_SCALE: Record<Purpose, {max: number; step: number}> = {
  ban: {max: 50_000_000_000, step: 100_000_000},
  'cho-thue': {max: 200_000_000, step: 1_000_000},
};

const AREA_MAX = 300;

function blankFilters(purpose: Purpose = 'ban'): Filters {
  return {
    amenities: [],
    areaMin: 0,
    bedrooms: 0,
    district: 'all',
    only360: false,
    onlyVerified: false,
    priceMax: PRICE_SCALE[purpose].max,
    province: 'all',
    purpose,
    query: '',
    sort: 'moi',
    type: 'all',
  };
}

function activeCount(filters: Filters): number {
  const base = blankFilters(filters.purpose);
  let count = 0;
  if (filters.province !== base.province) count += 1;
  if (filters.district !== base.district) count += 1;
  if (filters.type !== base.type) count += 1;
  if (filters.priceMax !== base.priceMax) count += 1;
  if (filters.areaMin !== base.areaMin) count += 1;
  if (filters.bedrooms !== base.bedrooms) count += 1;
  if (filters.only360) count += 1;
  if (filters.onlyVerified) count += 1;
  count += filters.amenities.length;
  return count;
}

/**
 * Thị trường — the app that opens first, and the only one a visitor needs.
 *
 * Two decisions shape it. The purpose switch (Bán / Cho thuê) is a segmented
 * control rather than a filter row, because it changes what every other number
 * on screen MEANS — a price slider that runs to 50 tỷ is nonsense for a rental.
 * And the filters live in a panel with a count on the button: a filter you
 * cannot see is a filter you forget you set, which is how people conclude a
 * marketplace is empty.
 */
export function MarketApp() {
  const {t} = useI18n();
  const {listings, ready} = useStore();
  const [filters, setFilters] = useState<Filters>(() => blankFilters('ban'));
  const [showFilters, setShowFilters] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const SORTS_I18N: Array<{label: string; value: Sort}> = [
    {label: t('market.sortNewest') || 'Mới nhất', value: 'moi'},
    {label: t('market.sortPriceAsc') || 'Giá thấp → cao', value: 'gia-tang'},
    {label: t('market.sortPriceDesc') || 'Giá cao → thấp', value: 'gia-giam'},
    {label: t('market.sortAreaDesc') || 'Diện tích lớn nhất', value: 'dien-tich'},
    {label: t('market.sortPriceM2Asc') || 'Giá / m² thấp nhất', value: 'gia-m2'},
  ];

  useIntent<MarketIntent>('market', (intent) => {
    if (intent.query !== undefined || intent.only360 !== undefined) {
      setFilters((prev) => ({
        ...prev,
        only360: intent.only360 ?? prev.only360,
        query: intent.query ?? prev.query,
      }));
    }
    if (intent.listingId) setOpenId(intent.listingId);
  });

  const patch = (next: Partial<Filters>) => setFilters((prev) => ({...prev, ...next}));

  const results = useMemo(() => {
    const needle = filters.query.trim().toLowerCase();
    const scale = PRICE_SCALE[filters.purpose];

    const rows = listings.filter((listing) => {
      if (!isPublic(listing)) return false;
      if (listing.purpose !== filters.purpose) return false;
      if (filters.province !== 'all' && listing.province !== filters.province) return false;
      if (filters.district !== 'all' && listing.district !== filters.district) return false;
      if (filters.type !== 'all' && listing.type !== filters.type) return false;
      if (filters.priceMax < scale.max && listing.price > filters.priceMax) return false;
      if (listing.area < filters.areaMin) return false;
      if (filters.bedrooms > 0 && listing.bedrooms < filters.bedrooms) return false;
      if (filters.only360 && !has360(listing)) return false;
      if (filters.onlyVerified && !listing.verified) return false;
      if (filters.amenities.some((item) => !listing.amenities.includes(item))) return false;
      if (!needle) return true;
      return [listing.title, listing.address, listing.district, listing.province, listing.code]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });

    const sorted = rows.slice();
    sorted.sort((a, b) => {
      switch (filters.sort) {
        case 'gia-tang':
          return a.price - b.price;
        case 'gia-giam':
          return b.price - a.price;
        case 'dien-tich':
          return b.area - a.area;
        case 'gia-m2':
          return pricePerM2(a) - pricePerM2(b);
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return sorted;
  }, [filters, listings]);

  const opened: Listing | null = openId
    ? (listings.find((listing) => listing.id === openId) ?? null)
    : null;
  const count = activeCount(filters);
  const scale = PRICE_SCALE[filters.purpose];

  return (
    <Screen
      toolbar={
        <Toolbar>
          <Segmented
            value={filters.purpose}
            onChange={(purpose) =>
              setFilters((prev) => ({
                ...blankFilters(purpose),
                query: prev.query,
                sort: prev.sort,
              }))
            }
            options={[
              {label: t('market.buy') || 'Nhà bán', value: 'ban'},
              {label: t('market.rent') || 'Cho thuê', value: 'cho-thue'},
            ]}
          />
          <SearchInput
            value={filters.query}
            onChange={(query) => patch({query})}
            placeholder={t('market.searchPlaceholder') || 'Khu vực, dự án, mã tin…'}
          />
          <Select
            value={filters.sort}
            onChange={(sort) => patch({sort: sort as Sort})}
            options={SORTS_I18N}
          />
          <Button
            icon={<SlidersHorizontal size={16} />}
            tone={count > 0 ? 'primary' : 'ghost'}
            onClick={() => setShowFilters(true)}
          >
            {t('market.filters') || 'Bộ lọc'}{count > 0 ? ` · ${count}` : ''}
          </Button>
        </Toolbar>
      }
    >
      <p className="mb-4 text-[12px] text-ink-3">
        {ready ? (t('market.resultsCount', {count: results.length}) || `${number(results.length)} tin đăng`) : 'Đang tải tin đăng…'}
        {count > 0 ? ' khớp bộ lọc' : ''}
      </p>

      {results.length === 0 && ready ? (
        <EmptyState
          title={t('market.noResults') || 'Chưa có tin nào khớp bộ lọc'}
          hint="Thử nới khoảng giá, bỏ bớt tiện ích hoặc chọn khu vực rộng hơn."
        />
      ) : (
        <div className="grid gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
          {results.map((listing) => (
            <ListingCard key={listing.id} listing={listing} onOpen={() => setOpenId(listing.id)} />
          ))}
        </div>
      )}

      {showFilters ? (
        <Panel
          title="Bộ lọc"
          onClose={() => setShowFilters(false)}
          footer={
            <div className="flex items-center justify-between gap-2">
              <Button
                icon={<RotateCcw size={15} />}
                onClick={() => setFilters(blankFilters(filters.purpose))}
              >
                Xoá lọc
              </Button>
              <Button tone="primary" onClick={() => setShowFilters(false)}>
                Xem {number(results.length)} tin
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid gap-4 @lg:grid-cols-2">
              <Field label="Tỉnh / thành">
                <Select
                  value={filters.province}
                  onChange={(province) => patch({district: 'all', province})}
                  options={[
                    {label: 'Toàn quốc', value: 'all'},
                    ...PROVINCES.map((value) => ({label: value, value})),
                  ]}
                />
              </Field>
              <Field label="Quận / huyện">
                <Select
                  value={filters.district}
                  onChange={(district) => patch({district})}
                  options={[
                    {label: 'Mọi khu vực', value: 'all'},
                    ...(filters.province === 'all'
                      ? Object.values(DISTRICTS).flat()
                      : districtsOf(filters.province)
                    ).map((value) => ({label: value, value})),
                  ]}
                />
              </Field>
              <Field label="Loại bất động sản" wide>
                <Select
                  value={filters.type}
                  onChange={(type) => patch({type})}
                  options={[
                    {label: 'Mọi loại', value: 'all'},
                    ...Object.entries(LISTING_TYPE).map(([value, label]) => ({
                      label,
                      value: value as ListingType,
                    })),
                  ]}
                />
              </Field>
            </div>

            <RangeField
              label="Giá tối đa"
              min={0}
              max={scale.max}
              step={scale.step}
              value={filters.priceMax}
              onChange={(priceMax) => patch({priceMax})}
              format={(value) =>
                value >= scale.max
                  ? 'Không giới hạn'
                  : `${money(value)}${filters.purpose === 'cho-thue' ? '/tháng' : ''}`
              }
            />

            <RangeField
              label="Diện tích tối thiểu"
              min={0}
              max={AREA_MAX}
              step={5}
              value={filters.areaMin}
              onChange={(areaMin) => patch({areaMin})}
              format={(value) => (value === 0 ? 'Không giới hạn' : `từ ${value} m²`)}
            />

            <div>
              <p className="mb-2 text-[12px] font-semibold text-ink-2">Số phòng ngủ</p>
              <div className="flex flex-wrap gap-2">
                {[0, 1, 2, 3, 4].map((value) => (
                  <ChipToggle
                    key={value}
                    active={filters.bedrooms === value}
                    onClick={() => patch({bedrooms: value})}
                  >
                    {value === 0 ? 'Tất cả' : `${value}+`}
                  </ChipToggle>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-[12px] font-semibold text-ink-2">Tiện ích</p>
              <div className="flex flex-wrap gap-2">
                {AMENITIES.map((item) => (
                  <ChipToggle
                    key={item}
                    active={filters.amenities.includes(item)}
                    onClick={() =>
                      patch({
                        amenities: filters.amenities.includes(item)
                          ? filters.amenities.filter((entry) => entry !== item)
                          : [...filters.amenities, item],
                      })
                    }
                  >
                    {item}
                  </ChipToggle>
                ))}
              </div>
            </div>

            <div className="sunken-sm rounded-card px-3 py-1">
              <CheckRow
                checked={filters.only360}
                onChange={(only360) => patch({only360})}
                label="Chỉ tin có ảnh 360°"
              />
              <CheckRow
                checked={filters.onlyVerified}
                onChange={(onlyVerified) => patch({onlyVerified})}
                label="Chỉ tin đã xác minh"
              />
            </div>
          </div>
        </Panel>
      ) : null}

      {opened ? (
        <Panel wide title={opened.code} onClose={() => setOpenId(null)}>
          <ListingDetail listing={opened} />
        </Panel>
      ) : null}
    </Screen>
  );
}
